import React, { useEffect, useState } from 'react';
import moment from 'moment';
import ServiceCollection from 'src/services/ServiceCollection';
import ATPHorizontalRadioGroup from '../shared/ATPHorizontalRadioGroup';
import { Alert, Button, Checkbox, FormField, SpaceBetween, StatusIndicator, Textarea } from '@amzn/awsui-components-react';
import { ACTION_TYPE, TPEBasicModal } from '../shared/TPEBasicModal';
import CONSTANTS from 'src/utils/constants';
import { GlobalAppContext } from '../App';
import WebSocketApi from 'src/services/WebSocketApi';
import { AppModules } from 'src/models/permissions/RolePermissions';
import { getPermissions } from '../AppPermissions';
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import ErrorUtils from 'src/utils/ErrorUtils';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import { BulkExecutionCounts, BulkPreflightResult, BulkPromoteActionResponse, BulkPromoteRequest, BulkPromotionDetail, isPreflightOperatorReport } from 'src/models/promotion/BulkPromotionTypes';
import { BULK_PROMOTE_TERMINAL_STATUSES } from 'src/models/promotion/bulkPromotionEnums';
import { BULK_PROMOTE_BATCH_CAP } from 'src/models/promotion/bulkPromotionConstants';
import { isAwaitingConfirmation, readBatchCounts, readBatchId, readBatchStatus } from 'src/components/calculations/bulk-promote/bulkPromotionRecord';
import { isFailureReason } from './bulk-promote/bulkPromotionBands';
import BulkPromoteFields from './bulk-promote/BulkPromoteFields';
import IdentifierSummary from './bulk-promote/IdentifierSummary';
import PreflightSummary from './bulk-promote/PreflightSummary';
import BulkPromoteProgress from './bulk-promote/BulkPromoteProgress';
import BulkPromoteResults from './bulk-promote/BulkPromoteResults';
import useBatchHandle, { createBatchHandle } from './bulk-promote/BatchHandle';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';
import { ACTIONS, bulkPromotionReducer } from 'src/services/bulk-promotion/BulkPromotionReducer';
import {
    BulkPromoteStep,
    VERDICT_PATIENCE_MILLIS,
    initialState as initialBulkPromotionState,
} from 'src/services/bulk-promotion/BulkPromotionState';
import { MAX_PROMOTION_POLL_ATTEMPTS, PROMOTION_POLL_INTERVAL_MS } from 'src/models/promotion/PromotionTypes';
import { isNameResolvable } from 'src/components/calculations/bulk-promote/bulkNameResolver';

export const BULK_PROMOTE_ACTION = 'PROMOTE';
const BULK_PROMOTE_PREFLIGHT = 'PROMOTE_PREFLIGHT';
const BULK_PROMOTE_EXECUTE = 'PROMOTE_EXECUTE';
/**
 * Releases a batch parked on its verdict without promoting anything.
 *
 * A parked batch holds an account-wide lock: the service reads any non-terminal batch as in flight across
 * every component type and period, so until one is released or its consent window lapses, no other bulk
 * promotion can start.
 */
const BULK_PROMOTE_ABANDON = 'PROMOTE_ABANDON';

/** Button label per action, so the button says what it will do. */
export const ACTION_BUTTON_LABEL: Record<string, string> = {
    SUBMIT: 'Submit',
    APPROVE: 'Approve',
    VALIDATE: 'Validate',
    CREATE_DRAFT: 'Create drafts',
    [BULK_PROMOTE_ACTION]: 'Check before promoting',
};

/**
 * Progress payload for the promote actions. The field names are BulkPromoteActionResponse's, which
 * mirror PerformBulkActionResponse in the Smithy model.
 */
type BulkPromoteProgressMessage = BulkPromoteActionResponse;

/**
 * Recognises a promote message without relying on a verdict arriving in the submit response.
 *
 * WebSocketMessage carries only connectionId, message, messageType and status, so there is no bulkAction
 * to switch on. The action is tested first in case the service starts echoing it; the payload's own shape
 * is the fallback that fires today.
 */
function isPromoteEnvelope(webSocketMessage: any, payload: BulkPromoteProgressMessage): boolean {
    if (
        webSocketMessage?.bulkAction === BULK_PROMOTE_PREFLIGHT ||
        webSocketMessage?.bulkAction === BULK_PROMOTE_EXECUTE
    ) {
        return true;
    }
    return (
        payload.batchId != null ||
        payload.preflightResult != null ||
        payload.executionResult != null ||
        payload.preflight != null ||
        payload.execution != null
    );
}

/** The verdict, under the Smithy name first and the older one second. */
function readPreflight(payload: BulkPromoteProgressMessage) {
    return payload.preflightResult ?? payload.preflight;
}

/** The outcome, under the Smithy name first and the older one second. */
function readExecution(payload: BulkPromoteProgressMessage) {
    return payload.executionResult ?? payload.execution;
}

/**
 * Whether an execution payload is a finished batch rather than the acknowledgement that one has
 * started.
 *
 * The confirm response is a BulkExecutionResult too — BulkPromotionSubmitComponent builds one with
 * the batch id, the counts so far and the validation mode, and returns it as soon as StartExecution
 * succeeds. It carries no perItem, because at that moment no item has an outcome. The completion
 * push carries perItem, empty if nothing was promotable. So presence of the array, not its length,
 * is what separates the two, and treating the acknowledgement as a result showed the operator a
 * finished batch that had barely started.
 *
 * `perItem` is declared non-optional on BulkExecutionResult but has no `@required` in
 * bulk-promotion-shapes.smithy, so the type cannot be trusted to guarantee it here.
 */
function isFinishedExecution(execution: any): boolean {
    return Array.isArray(execution?.perItem);
}

/**
 * Whether a verdict accounts for every component the batch accepted.
 *
 * Each component ends up in exactly one of promotable, blocked or skipped, so those three have to
 * add up to acceptedCount. A payload that does not add up is a partial or placeholder verdict, and
 * rendering it states a false outcome — understating what would promote invites the operator to
 * cancel a promotion that would have succeeded, and in a mixed batch the count they consent to is
 * not the count that promotes. Frames that arrive incomplete are merged by VERDICT_FRAME instead,
 * which tracks the pages it has seen.
 */
function isCompleteVerdict(verdict: any): boolean {
    if (!Array.isArray(verdict?.promotable) || !Array.isArray(verdict?.blocked)) {
        return false;
    }
    const accountedFor = verdict.promotable.length + verdict.blocked.length + (verdict.skipped?.length ?? 0);
    // A verdict with no acceptedCount cannot be checked, so it is taken at face value rather than
    // suppressed: withholding a verdict the operator is waiting for is the worse failure.
    return verdict.acceptedCount == null || accountedFor >= verdict.acceptedCount;
}

/**
 * Rebuilds the verdict for a batch found parked on AWAITING_CONFIRMATION, from the durable record
 * rather than from a push that has already been and gone.
 *
 * The record keeps failures and skips in one list of blocked rows, so they are split here on the same
 * rule the service applies when it builds a report — UNCHANGED_SINCE_LAST_PROMOTION is not a failure.
 * Folding the skips in with the blocks would make a re-run that had already converged read as a batch
 * full of problems, which is precisely the misreading this reason exists to prevent.
 *
 * What would promote is derived, not stored: promotedIds is empty until the batch runs, so it is the
 * submitted set less everything the record accounts for.
 *
 * That set includes failedIds as well as the blocked rows. A component that failed during execution has no
 * blocked row — it was accepted and then failed — so counting only the blocked rows leaves it in the
 * promotable set, and the terminal and recovery paths render it as promoted.
 */
/**
 * The validation mode as recorded on the batch, read from configSnapshot rather than from the detail's own
 * validationMode field.
 *
 * Both exist on the wire, but only configSnapshot is populated: GetBulkPromotionsComponent sets it from the
 * header, while nothing sets validationMode on the summary or detail — BulkPromotionMapper has no reference
 * to it. The consent screen's verdict is rebuilt from this record, so without reading the snapshot the mode
 * is always absent there and every batch is reported as not fully validated. Confirmed in alpha on
 * 2026-09-22: batch 4ce6ff17 recorded {"validationMode":"PARITY"} and promoted 3 of 3.
 *
 * Returns '' rather than undefined when it cannot be read, so an unreadable snapshot fails closed.
 */
export function validationModeFromSnapshot(snapshot?: string): string {
    if (!snapshot) {
        return '';
    }
    try {
        const parsed = JSON.parse(snapshot);
        return typeof parsed?.validationMode === 'string' ? parsed.validationMode : '';
    } catch {
        return '';
    }
}

export function rebuildParkedVerdict(detail: BulkPromotionDetail, counts: BulkExecutionCounts): BulkPreflightResult {
    const rows = detail.blocked || [];
    const blocked = rows.filter((row) => isFailureReason(row.reason));
    const skipped = rows.filter((row) => !isFailureReason(row.reason)).map((row) => row.componentId);
    const failed = detail.failedIds || [];
    const accountedFor = new Set([...rows.map((row) => row.componentId), ...failed]);
    const submitted = detail.submittedIds || [];
    const promotable = submitted.length > 0
        ? submitted.filter((componentId) => !accountedFor.has(componentId))
        : (detail.promotedIds || []).filter((componentId) => !accountedFor.has(componentId));

    return {
        promotable,
        blocked,
        prerequisites: detail.prerequisites || [],
        // The submitted set is what was accepted. Falling back to the recorded count keeps this
        // honest when submittedIds is not projected into the response.
        acceptedCount: submitted.length > 0 ? submitted.length : counts.requested,
        validationMode: detail.validationMode || validationModeFromSnapshot(detail.configSnapshot),
        skipped,
        blockedCount: blocked.length,
    };
}

export default function BulkActionsView(props: {services: ServiceCollection}) {
    const { services } = props;
    const { userProfile } = React.useContext(GlobalAppContext);
    const bulkActionsPermissions = getPermissions(AppModules.BULK_ACTIONS, userProfile);

    /**
     * The open period, needed to read a batch back: `accountingPeriod` is `@required` on
     * GetBulkPromotionRequest, so a read without it is rejected. Sourced the same way the promotion
     * history screen sources it, and formatted the same way, so the two cannot ask for different
     * periods for the same batch. A batch is always submitted into the open period, so the open period
     * is the right one to read it back from.
     */
    const [handOffQuery] = useState((new Date()).toString());
    // The error slot is read, not dropped. Without it an unreadable open period left openPeriod at '' with
    // nothing said: recoverBatch bannered "the open accounting period is not available yet. Retry in a
    // moment." — which never becomes true and names the wrong cause — and the poller returned silently every
    // tick, leaving the page on "Promoting" for a batch it had stopped reading. PromotionHistoryView reads
    // the same slot; this screen was the one discarding it.
    const [openPeriodRaw, , , handOffError] = services.mecService.getHandOffStatus(handOffQuery);
    /*
     * Validity is checked before the value is used. An invalid moment formats as the locale string
     * 'Invalid date', which uppercased to the non-empty 'INVALID DATE' — truthy, so it passed every
     * `if (!period)` guard, went out as the @required accountingPeriod and was persisted into the batch
     * handle. The record is then never found: recovery reports the batch as expired and the poll returns
     * silently on every tick, leaving the page on "Promoting" for a batch that finished.
     *
     * Deliberately NOT parsed strictly. PromotionHistoryView derives the same value the same lenient way,
     * and whether getHandOffStatus returns `YYYYMM` or an ISO date is not settled in this package — a strict
     * parse that rejected the real format would blank the period and disable the read path altogether, which
     * is worse than the bug being fixed. isValid() closes the 'INVALID DATE' hole either way.
     */
    const openPeriodMoment = openPeriodRaw ? moment(openPeriodRaw, CONSTANTS.PERIOD_VALUE_FORMAT) : null;
    const openPeriod = openPeriodMoment && openPeriodMoment.isValid()
        ? openPeriodMoment.format('MMM-YY').toUpperCase()
        : '';

    // Off until the flag says otherwise, matching DEFAULT_FLAGS: the action must not appear on the first
    // render and then vanish, and a stage with the flag unset is a stage whose backend route may not be
    // wired, where offering the action would fail at submit.
    const [bulkPromotionEnabled, setBulkPromotionEnabled] = useState(false);
    useEffect(() => {
        getFeatureFlags()
            .then(flags => setBulkPromotionEnabled(flags.bulkPromotionEnabled))
            .catch(ex => {
                setBulkPromotionEnabled(false);
                services.messageService.showErrorBanner(`Feature flags could not be read: ${ErrorUtils.getMessage(ex)}`);
            });
    }, []);

    // Surfaced once when it changes, not per render: a banner per render would bury the screen.
    useEffect(() => {
        if (handOffError) {
            services.messageService.showErrorBanner(
                `The open accounting period could not be read, so a batch cannot be submitted or read back: `
                + `${handOffError}`);
        }
    }, [handOffError]);

    // The grant alone decides who sees this; no branch below distinguishes a tech operator from a
    // self-service user.
    const bulkPromoteAvailable = bulkPromotionEnabled && bulkActionsPermissions.canBulkPromote;

    const bulkActionOptions = [
        { value: 'SUBMIT', label: 'Submit calculations' },
        { value: 'APPROVE', label: 'Approve calculations' },
        { value: 'VALIDATE', label: 'Validate group calculations'},
        { value: 'CREATE_DRAFT', label: 'Create draft calculations'},
        ...(bulkPromoteAvailable ? [{ value: BULK_PROMOTE_ACTION, label: 'Promote components' }] : []),
    ];

    const [selectedAction, setSelectedAction] = useState(bulkActionOptions[0].value);
    const [calculations, setCalculations] = useState(null as any as string);
    const [checked, setChecked] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [result, setResult] = useState(null as unknown as any);
    const [progressUpdate, setProgressUpdate] = useState(null as unknown as string);
    const [loading, setLoading] = useState(false);

    const [promoteState, promoteDispatch] = useReducerWithLogger(bulkPromotionReducer, initialBulkPromotionState);

    /*
     * Fetches the (id, name) list for the selected type, so a pasted NAME resolves to the id a promotion
     * submits. MDA and TPA components are addressed by an opaque id — a UUID or a generated slug — while the
     * name an operator can read off a screen is a different string entirely.
     *
     * Reuses the list operations the MDA and TPA screens already call, so there is no new API. Skipped
     * entirely for CLI_CONFIG and PLI_CONFIG: isNameResolvable is false for them, listNamedComponents returns
     * [] anyway, and their ids ARE the readable identifier.
     *
     * A failure is deliberately not bannered. Name resolution is a convenience on top of ids, so losing it
     * must not look like the screen is broken — pasted ids keep working and a pasted name reports as not
     * found, which is what it is from this page's point of view.
     */
    useEffect(() => {
        if (!isNameResolvable(promoteState.componentType)) {
            promoteDispatch(ACTIONS.SET_NAMED_COMPONENTS.withPayload([]));
            return;
        }
        let cancelled = false;
        services.bulkPromotionService.listNamedComponents(promoteState.componentType as string)
            .then(components => {
                if (!cancelled) {
                    promoteDispatch(ACTIONS.SET_NAMED_COMPONENTS.withPayload(components));
                }
            })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [promoteState.componentType])

    const [batchHandle, setBatchHandle] = useBatchHandle();

    /**
     * The period every read of a batch goes through, in order of authority.
     *
     * The batch's own period first: it was submitted into a period and that never changes. Then the period the
     * service reported with the configuration, which is the same value the submit path stamps a batch with.
     * The screen's own getHandOffStatus derivation is last, because it is empty until that lookup resolves,
     * and an empty period is dropped from the request — which the gateway refuses, leaving the page unable to
     * report a verdict for a batch that has already promoted.
     *
     * Empty here means no read is possible yet, and callers are expected to check rather than send a request
     * that cannot succeed.
     */
    const readBackPeriod = batchHandle?.accountingPeriod || openPeriod;

    const isPromote = selectedAction == BULK_PROMOTE_ACTION;

    /*
     * Open the socket as soon as bulk promotion is reachable, rather than at the moment the operator submits.
     *
     * The handshake crosses two unprovisioned Java Lambdas in series and was measured at 11.4 seconds cold in
     * alpha (authorizer init 1.6s, $connect handler init 4.0s). Paying that when the button is pressed means
     * the operator waits through it with a batch of ids ready to go, and any timeout there reads as "the
     * server is unreachable". Paying it here spends the cold start while they are still pasting, so by submit
     * time the socket is normally already open and waitUntilConnected returns on its first check.
     *
     * Deliberately fire-and-forget: this is a warm-up, not a precondition. Failure is not surfaced, because
     * every send path awaits waitUntilConnected and reports for itself — a warm-up that failed just means the
     * cold start is paid later, which is the behaviour this exists to avoid rather than an error in itself.
     */
    useEffect(() => {
        if (!bulkPromoteAvailable) {
            return;
        }
        services.atpWebSocketApiService.ensureConnected();
    }, [bulkPromoteAvailable])

    // The listener is registered once and never re-registered, so it cannot close over promote state.
    // A ref is how it reads the current step without the effect having to depend on it — re-running
    // the effect would drop and re-add the listener on every state change.
    const promoteAwaitingRef = React.useRef(false);
    promoteAwaitingRef.current =
        promoteState.step === BulkPromoteStep.AWAITING_VERDICT || promoteState.step === BulkPromoteStep.EXECUTING;

    useEffect(() => {
        const { listener, errorHandler } = setupWebSocketEvents(services, setProgressUpdate, setResult, setLoading,
            promoteDispatch, () => promoteAwaitingRef.current);
        return () => {
            // Anything in here is fired on component unmount.
            services.atpWebSocketApiService.removeListener('',listener);
            services.atpWebSocketApiService.removeListener(WebSocketApi.ConnectionEvent.ON_ERROR, errorHandler);
        }
    }, [])

    // Ends the indefinite spinner when a verdict never arrives. The batch is not cancelled and a
    // verdict delivered afterwards is still taken; all that changes is that the operator is told,
    // and offered the read-back path instead of being left to guess.
    useEffect(() => {
        if (promoteState.step !== BulkPromoteStep.AWAITING_VERDICT || promoteState.awaitingVerdictSince === null) {
            return;
        }
        const elapsed = Date.now() - promoteState.awaitingVerdictSince;
        const timer = setTimeout(() => promoteDispatch(ACTIONS.VERDICT_OVERDUE),
            Math.max(0, VERDICT_PATIENCE_MILLIS - elapsed));
        return () => clearTimeout(timer);
    }, [promoteState.step, promoteState.awaitingVerdictSince])

    // Persisted as soon as the batch has an id, not at confirmation. A preflight whose verdict never
    // arrives is exactly the case the handle exists for, and storing it only at confirm meant the one
    // batch an operator could not see the outcome of was also the one they could not recover.
    useEffect(() => {
        if (promoteState.batchId && (!batchHandle || batchHandle.batchId !== promoteState.batchId)) {
            setBatchHandle(createBatchHandle(promoteState.batchId, promoteState.componentType || '',
                new Date().toISOString(), readBackPeriod));
        }
    }, [promoteState.batchId])

    // A terminal outcome has been shown, so the handle has served its purpose.
    useEffect(() => {
        if (promoteState.execution && batchHandle && batchHandle.batchId === promoteState.execution.batchId) {
            setBatchHandle(null);
        }
    }, [promoteState.execution?.batchId])

    /**
     * Reads a batch back from its durable record. The only recovery for a push that cannot be
     * redelivered, and the way in for a batch left parked by an earlier session.
     *
     * The id, status and counts are read through helpers because two spellings are in play: the
     * Smithy shapes call them batchId, status and counts, while the shapes declared in
     * BulkPromotionTypes call them bulkPromotionId, bulkPromotionStatus and five flat fields. Reading
     * one spelling only is what made this silently useless — `bulkPromotionStatus` came back
     * undefined, no status is in the terminal set, so every batch reported as still running.
     */
    const recoverBatch = () => {
        if (!batchHandle) {
            return;
        }
        /*
         * The batch's own period first, the open period only as a fallback. The period is a property of the
         * batch, not of the current screen: reading through the open period alone fails whenever
         * getHandOffStatus has not resolved, because an empty period is dropped and the request refused.
         */
        const period = readBackPeriod;
        if (!period) {
            services.messageService.showErrorBanner(
                `Cannot read batch ${batchHandle.batchId} back: the open accounting period is not `
                + `available yet. Retry in a moment.`);
            return;
        }
        promoteDispatch(ACTIONS.SET_LOADING.withPayload(true));
        services.bulkPromotionService.getBulkPromotion(batchHandle.batchId, period)
            .then(detail => {
                if (!detail) {
                    services.messageService.showErrorBanner(
                        `Batch ${batchHandle.batchId} could not be found. It may have expired.`);
                    return;
                }
                const batchId = readBatchId(detail) || batchHandle.batchId;
                const status = readBatchStatus(detail);
                const counts = readBatchCounts(detail);

                /*
                 * The component type is restored before anything renders. SET_COMPONENT_TYPE has only one
                 * other dispatcher — the dropdown — so on every recovery path the type stayed null, and
                 * formatIdsForClipboard skips re-encoding without one. For the base64 types that made
                 * "Copy all" hand back bare decoded ids, which the paste box then refuses as RAW_UUID: the
                 * corrective re-run the button exists for could not be performed. The record's own type is
                 * preferred over the stored handle's, which can be an empty string.
                 */
                const recoveredType = (detail as any).componentType || batchHandle.componentType;
                if (recoveredType) {
                    promoteDispatch(ACTIONS.SET_COMPONENT_TYPE.withPayload(recoveredType));
                }

                // Parked on its verdict: the machine has stopped and will not move again until the
                // operator confirms, so this is neither running nor finished. Rebuilding the
                // confirmation from the record is what lets a batch survive a closed tab.
                if (isAwaitingConfirmation(status)) {
                    const parked = rebuildParkedVerdict(detail, counts);
                    promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
                        batchId,
                        acceptedCount: parked.acceptedCount,
                    }));
                    promoteDispatch(ACTIONS.SET_VERDICT.withPayload(parked));
                    services.messageService.showInfoAutoDismissBanner(
                        `Batch ${batchId} is waiting for your confirmation.`);
                    return;
                }

                if (!BULK_PROMOTE_TERMINAL_STATUSES.has(status || '')) {
                    services.messageService.showInfoAutoDismissBanner(`Batch ${batchId} is still running.`);
                    /*
                     * Handed to the poller rather than left as a snapshot. The poll is gated on the batch id
                     * and on the step being watched, so without both the operator would have to press the
                     * button again for every update — on the reload path this recovery exists for.
                     */
                    promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
                        batchId,
                        acceptedCount: counts.requested,
                    }));
                    promoteDispatch(ACTIONS.EXECUTE_SUBMITTED);
                    if (detail.phase) {
                        promoteDispatch(ACTIONS.SET_PHASE.withPayload({ phase: detail.phase, barrierWaitMillis: detail.barrierWaitMillis }));
                    }
                    return;
                }
                /*
                 * The verdict is rebuilt from the record before the outcome is shown, because the outcome
                 * table renders from it. BulkPromoteResults falls back to deriveOutcomesFromVerdict
                 * whenever the service sends no perItem — which is always, since BulkExecutionResult has
                 * no perItem member — so with preflight left null the table had nothing to derive from and
                 * printed "No outcomes recorded" under a header counting the very rows it was missing.
                 *
                 * The rows were never absent: detail.blocked carries every refused component with its
                 * reason, detail and blocking dependency, and this fetch already has them in hand. The
                 * same rebuild the AWAITING_CONFIRMATION branch above performs is correct here too, so it
                 * is reused rather than duplicated — it also splits UNCHANGED out of blocked, which keeps
                 * a re-run of already-converged components reading as skipped rather than as failures.
                 */
                /*
                 * The batch is adopted before its verdict, exactly as the AWAITING_CONFIRMATION branch above
                 * does. The reducer refuses a verdict and an outcome that arrive in INPUT with no batch
                 * adopted, because that is how a late delivery for a flow the operator has left presents —
                 * so a recovery that skips this step is indistinguishable from one, and was silently dropped.
                 */
                const terminalVerdict = rebuildParkedVerdict(detail, counts);
                promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
                    batchId,
                    acceptedCount: terminalVerdict.acceptedCount,
                }));
                promoteDispatch(ACTIONS.SET_VERDICT.withPayload(terminalVerdict));
                promoteDispatch(ACTIONS.EXECUTION_COMPLETE.withPayload({
                    batchId,
                    /*
                     * The terminal status is carried, not dropped. Every terminal status was previously
                     * discarded here and the reducer forced phase COMPLETE, so SUCCEEDED, PARTIALLY_SUCCEEDED,
                     * FAILED and ABANDONED all rendered as one green "Complete" — and ABANDONED is 9 of 49
                     * batches in alpha, i.e. a batch whose consent window lapsed with nothing attempted read
                     * as a success.
                     */
                    status,
                    /*
                     * And so are the components that failed during execution. failedIds is a real member of
                     * the by-id read and the service writes it from three execution-phase call sites (up to
                     * 467 components on one alpha batch); it was in hand here and used only to subtract from
                     * promotable, so the header printed "N failed" above a table that could not name one.
                     */
                    failedIds: detail.failedIds || [],
                    // Still empty: per-item execution outcomes are a service gap, not one this page can
                    // close. What the record holds is the preflight verdict, dispatched above, and the
                    // table derives from that.
                    perItem: [],
                    counts,
                    prerequisites: detail.prerequisites || [],
                    // Same snapshot fallback as rebuildParkedVerdict. detail.validationMode is never
                    // populated — GetBulkPromotions has no mapping for it — while config_snapshot carries
                    // the real value, so reading the field alone handed the results screen `undefined` and
                    // the fail-closed check then warned "dependencies were not checked" on every PARITY batch.
                    validationMode: detail.validationMode || validationModeFromSnapshot(detail.configSnapshot),
                    barrierWaitMillis: detail.barrierWaitMillis || 0,
                    configSnapshot: {},
                }));
            })
            .catch(ex => services.messageService.showErrorBanner(ErrorUtils.getMessage(ex)))
            .finally(() => promoteDispatch(ACTIONS.SET_LOADING.withPayload(false)));
    }

    /**
     * Polls a confirmed batch until it reaches a terminal status.
     *
     * Nothing pushes a completion: BulkCompleteComponent holds no WebSocket accessor, so
     * BulkReportComponent's verdict is the only message the bulk flow ever posts. Without this the
     * page stays on "Processing" for every batch, however it ends — a batch that promoted an hour ago
     * still reads as running. Recovery through the batch handle covers a closed tab, but it is a
     * button the operator has to know to press, and it does not help the one watching the screen.
     *
     * The record is authoritative rather than the push, so this is also correct when a push is added
     * later: whichever arrives first moves the flow on, and EXECUTION_COMPLETE is idempotent because
     * the step has already left EXECUTING by the time a second one lands.
     */
    useEffect(() => {
        /*
         * AWAITING_VERDICT is polled as well as EXECUTING. Nothing in the bulk package pushes the
         * preflight verdict — BulkStepInput carries connectionId but no arm posts to it, which the
         * field's own comment records — so during the wait the only delivery the backend offers is
         * this read. Polling only EXECUTING meant the verdict phase had no delivery at all: the page
         * sat on "Checking components" until VERDICT_PATIENCE_MILLIS elapsed and then declared the
         * verdict overdue, for a batch that had been parked and ready to confirm within seconds.
         */
        const watched = promoteState.step === BulkPromoteStep.EXECUTING
            || promoteState.step === BulkPromoteStep.AWAITING_VERDICT;
        if (!watched || !promoteState.batchId) {
            return undefined;
        }
        const period = readBackPeriod;
        if (!period) {
            return undefined;
        }
        const batchId = promoteState.batchId;
        let cancelled = false;
        const poll = () => {
            services.bulkPromotionService.getBulkPromotion(batchId, period)
                .then(detail => {
                    if (cancelled || !detail) {
                        return;
                    }
                    const status = readBatchStatus(detail);
                    if (detail.phase) {
                        promoteDispatch(ACTIONS.SET_PHASE.withPayload({
                            phase: detail.phase,
                            barrierWaitMillis: detail.barrierWaitMillis,
                        }));
                    }
                    /*
                     * Parked and ready: surface the verdict so the operator gets a confirm step. This
                     * is the outcome the AWAITING_VERDICT poll exists for, and it is not terminal, so
                     * the terminal check below would otherwise discard it and keep waiting forever.
                     */
                    if (isAwaitingConfirmation(status)) {
                        const parked = rebuildParkedVerdict(detail, readBatchCounts(detail));
                        promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
                            batchId: readBatchId(detail) || batchId,
                            acceptedCount: parked.acceptedCount,
                        }));
                        promoteDispatch(ACTIONS.SET_VERDICT.withPayload(parked));
                        return;
                    }
                    if (!BULK_PROMOTE_TERMINAL_STATUSES.has(status || '')) {
                        return;
                    }
                    /*
                     * The record's blocked rows become the verdict the outcome table derives from.
                     *
                     * This is the path a batch takes when every component is refused at preflight: the
                     * machine reaches a terminal status without emitting a report frame, so nothing populates
                     * the verdict and the outcome table has no rows to show — while the durable record holds
                     * every blocked row with its reason. Rebuilding from that record is what fills it.
                     */
                    const terminalCounts = readBatchCounts(detail);
                    const polledVerdict = rebuildParkedVerdict(detail, terminalCounts);
                    // Adopted first, for the reason given on the recovery branch above.
                    promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
                        batchId: readBatchId(detail) || batchId,
                        acceptedCount: polledVerdict.acceptedCount,
                    }));
                    promoteDispatch(ACTIONS.SET_VERDICT.withPayload(polledVerdict));
                    promoteDispatch(ACTIONS.EXECUTION_COMPLETE.withPayload({
                        batchId: readBatchId(detail) || batchId,
                        // Carried so the outcome screen can state which terminal it reached — see the note on
                        // the recovery dispatch above. ABANDONED must not render as a success.
                        status,
                        failedIds: detail.failedIds || [],
                        // Per-item execution outcomes remain a service gap on the EXECUTE ack — that shape
                        // declares no perItem — so this stays empty and the table derives from the verdict
                        // above. The id lists, unlike perItem, ARE on this read.
                        perItem: [],
                        counts: terminalCounts,
                        prerequisites: detail.prerequisites || [],
                        // Snapshot fallback, as on the recovery dispatch above: the field is never populated.
                        validationMode: detail.validationMode || validationModeFromSnapshot(detail.configSnapshot),
                        barrierWaitMillis: detail.barrierWaitMillis || 0,
                        configSnapshot: {},
                    }));
                })
                // Swallowed deliberately: a failed read is retried on the next tick, and bannering
                // every tick would bury the screen while the batch is still perfectly healthy.
                .catch(() => undefined);
        };
        poll();
        /*
         * Capped, like the single-promotion poller (usePromotion enforces the same constant, declared four
         * lines below the interval this file already imports). Uncapped, a batch whose tracker never reaches a
         * terminal status polled at ~1,200 reads an hour per open tab for as long as the tab lived — and
         * because the socket warm-up keeps a live connection, the tab is exempt from background-timer
         * throttling. On exhaustion the operator is told, rather than left watching "Promoting" forever.
         */
        let attempts = 0;
        const timer = setInterval(() => {
            attempts += 1;
            if (attempts > MAX_PROMOTION_POLL_ATTEMPTS) {
                clearInterval(timer);
                /*
                 * PUSH_LOST is dispatched, not just bannered. The banner tells the operator to use
                 * "Get its outcome", and that button lives inside the recovery banner, which is gated on
                 * `recoveryOffered` — step INPUT, or pushLost, or verdictOverdue. Exhausting the poll left the
                 * step at EXECUTING with neither flag set, so the button was not on screen and there is no
                 * independent patience timer in that state to set one: the operator was told to press
                 * something that did not exist, on a spinner that never stopped.
                 *
                 * PUSH_LOST is the honest description of what happened — this page can no longer learn the
                 * outcome — and it is accepted from EXECUTING. It also clears `loading`, which ends the spinner.
                 */
                promoteDispatch(ACTIONS.PUSH_LOST);
                services.messageService.showErrorBanner(
                    `Stopped reading batch ${promoteState.batchId} back after ${MAX_PROMOTION_POLL_ATTEMPTS} attempts.`
                    + ' The batch may still be running — use Get its outcome to read it again.');
                return;
            }
            poll();
        }, PROMOTION_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
        // period is included: the effect returns early without one, so it has to re-run when the open
        // period resolves or the batch handle supplies it, or the poll would never start.
    }, [promoteState.step, promoteState.batchId, readBackPeriod])

    const process = () => {
        if (isPromote) {
            submitPromotePreflight();
            return;
        }
        if (calculations == null || calculations.trim() == '') {
            if (selectedAction == 'VALIDATE') {
                services.messageService.showErrorBanner('A group number is required.');
            } else {
                services.messageService.showErrorBanner('At least one calculation number is required.');
            }
            return;
        }
        if (selectedAction == 'VALIDATE') {
            submitFormForValidation();
        } else {
            // Trim and remove duplicates
            const calcArr = calculations.split(',').filter(x => x.trim() != '').map(x => x.trim()).filter((value, index, self) => index == self.indexOf(value));
            setCalculations(calcArr.join(', '));
            if (calcArr.length == 0) {
                services.messageService.showErrorBanner('At least one calculation number is required.');
                return;
            }
            if (selectedAction == 'CREATE_DRAFT') {
                submitForm();
                return;
            }
            setShowConfirmation(true);
        }
    }

    /**
     * Phase one. What the operator confirms is this verdict, never the paste, so there is no
     * unconfirmed promote path.
     */
    const submitPromotePreflight = async () => {
        const { componentType, acceptedIds, overCapBy } = promoteState;
        if (!componentType) {
            services.messageService.showErrorBanner('Select a component type first.');
            return;
        }
        if (acceptedIds.length == 0) {
            services.messageService.showErrorBanner('At least one component ID is required.');
            return;
        }
        if (overCapBy > 0) {
            services.messageService.showErrorBanner(
                `${acceptedIds.length} components is ${overCapBy} over the limit of ${BULK_PROMOTE_BATCH_CAP}.`);
            return;
        }
        // The type and the identifiers, and nothing else. No actor field of any kind is sent: the
        // promoter is the authenticated principal the service resolves from the connection's
        // authorizer, and the promotion tracker records only that one. A client-supplied alias would
        // either be ignored or, worse, be gated on — a required actor field can refuse a financial
        // promotion over a value the item worker never passes to promote() anyway.
        const request: BulkPromoteRequest = {
            componentType,
            componentIds: acceptedIds,
        };
        /*
         * Dispatched only once the socket has taken it. sendMessage queues a message it cannot send and the
         * queue is flushed by onopen alone, so on a closed socket the submit sits in memory indefinitely.
         * Dispatching PREFLIGHT_SUBMITTED before that point puts the page into "Checking components" for a
         * batch the server was never told about, until the verdict is declared overdue.
         */
        // Awaited, because ensureConnected only STARTS a connection. Sending straight after it hits a socket
        // in CONNECTING state, which queues the message and reports "not sent" — while onopen then flushes the
        // queue and creates the batch anyway. That combination told the operator nothing had been submitted
        // while a batch was being created, then refused their retry because the first one held the lock.
        const ready = await services.atpWebSocketApiService.waitUntilConnected();
        const sent = ready && services.atpWebSocketApiService.performBulkAction(
            { bulkAction: BULK_PROMOTE_PREFLIGHT, bulkPromote: request });
        if (!sent) {
            services.messageService.showErrorBanner(
                'Could not reach the server to start the check. Nothing has been submitted — '
                + 'refresh the page and try again.');
            return;
        }
        promoteDispatch(ACTIONS.PREFLIGHT_SUBMITTED);
    }

    /**
     * Phase two. The batch id and nothing else: resending identifiers would allow a different set
     * to be executed from the one that was confirmed, and the actor is resolved server-side.
     */
    const confirmPromote = async () => {
        if (!promoteState.batchId) {
            return;
        }
        // Checked before anything else changes: a confirmation that never left must not look like one that
        // did. Consenting to a promotion is the one action in this flow an operator cannot take back, so
        // reporting it as submitted when the socket dropped it is worse here than anywhere else.
        const ready = await services.atpWebSocketApiService.waitUntilConnected();
        const sent = ready && services.atpWebSocketApiService.performBulkAction({
            bulkAction: BULK_PROMOTE_EXECUTE,
            batchId: promoteState.batchId,
        });
        if (!sent) {
            services.messageService.showErrorBanner(
                `Could not reach the server to confirm batch ${promoteState.batchId}. Nothing has been `
                + 'promoted — refresh the page and confirm again.');
            return;
        }
        promoteDispatch(ACTIONS.EXECUTE_SUBMITTED);
        // The handle is already stored — it is written as soon as the batch has an id, so that a
        // preflight whose verdict never arrives is recoverable too. Refreshed here so the timestamp
        // the alert shows is the confirmation the operator will remember making.
        //
        // Same precedence as readBackPeriod: the batch's own period first. The period is a property of the
        // batch, and preferring the open period here would overwrite it — after which every read-back and
        // poll, which resolve through the handle, would query the wrong period.
        setBatchHandle(createBatchHandle(promoteState.batchId, promoteState.componentType || '',
            new Date().toISOString(), batchHandle?.accountingPeriod || openPeriod || ''));
    }

    /**
     * Declining the verdict, which has to tell the server — not just clear this screen.
     *
     * The batch is parked on a task token, and the service reads any non-terminal batch as in flight across
     * every component type and accounting period. So a Cancel that only reset local state left the batch
     * holding that lock for the rest of its hour-long consent window: the operator saw a cleared screen and
     * believed they had cancelled, while every other bulk promotion in the account was refused. That was
     * worse than having no button at all, because it looked as though it worked.
     *
     * Local state is reset either way. If the release fails the operator is told, because the difference
     * matters to whoever tries the next batch, but leaving them stuck on a verdict they have already declined
     * would not help them.
     */
    const abandonPromote = async (explicitBatchId?: string | null) => {
        /*
         * The id is passed in rather than read from promoteState, because the recovery banner is reached after
         * a reload: promoteState is empty there and only the stored handle knows the batch. Reading it from
         * state alone made the banner's button a no-op on exactly the path an operator uses when a batch is
         * stuck, which is when they need it most.
         */
        const batchId = explicitBatchId || promoteState.batchId || batchHandle?.batchId;
        if (!batchId) {
            promoteDispatch(ACTIONS.RESET_STATE);
            return;
        }
        const ready = await services.atpWebSocketApiService.waitUntilConnected();
        const sent = ready && services.atpWebSocketApiService.performBulkAction({
            bulkAction: BULK_PROMOTE_ABANDON,
            batchId,
        });
        if (!sent) {
            /*
             * The handle is KEPT here. Clearing it told the operator the batch may still be blocking every
             * other bulk promotion and simultaneously removed the recovery banner — the only surface carrying
             * that batch id and the only place "Get its outcome" or a second "Release it" can be reached. The
             * release is idempotent server-side, so retrying is safe; the state is left alone so it can be.
             */
            services.messageService.showErrorBanner(
                `Could not reach the server to release batch ${batchId}. It may keep blocking other bulk `
                + 'promotions until its confirmation window closes. Try Release it again.');
            return;
        }
        setBatchHandle(null);
        promoteDispatch(ACTIONS.RESET_STATE);
    }

    const submitForm = () => {
        const calcArr = calculations.split(', ');
        const payload = {
            calculationNumbers: calcArr,
            bulkAction: selectedAction,
            onlyAllowedCalculations: checked,
            requestUser: userProfile.user
        }
        setLoading(true);
        setResult(null as unknown as any);
        setProgressUpdate(null as unknown as string);
        services.atpWebSocketApiService.performBulkAction(payload);
        services.messageService.showInfoAutoDismissBanner(`Processing ${calcArr.length} calculations.`);
        setShowConfirmation(false);
    }

    const submitFormForValidation = () => {
        const payload = {
            groupNumber: calculations.trim(),
            bulkAction: selectedAction,
            requestUser: userProfile.user
        }
        setLoading(true);
        setResult(null as unknown as any);
        setProgressUpdate(null as unknown as string);
        services.atpWebSocketApiService.performBulkAction(payload);
        services.messageService.showInfoAutoDismissBanner(`Processing group ${calculations.trim()}.`);
        setShowConfirmation(false);
    }

    const clear = () => {
        setCalculations(null as unknown as string);
        setResult(null as unknown as any);
        /*
         * A live batch is released rather than dropped. RESET_STATE alone wiped the batch id, the verdict and
         * the paste while the batch stayed open on the server, holding the account-wide lock for the rest of
         * its confirmation window with no id left anywhere in the UI — the silent cancel abandonPromote's own
         * docblock calls worse than having no button at all. Reachable because Clear is enabled whenever
         * `loading` is false, which PUSH_LOST and VERDICT_OVERDUE both make true mid-batch.
         */
        const liveBatchId = promoteState.batchId || batchHandle?.batchId;
        // Open, in step terms: past the form and not yet finished. BULK_PROMOTE_TERMINAL_STATUSES holds
        // BulkPromotionStatus values and must not be tested against a BulkPromoteStep.
        const batchIsOpen =
            promoteState.step !== BulkPromoteStep.INPUT && promoteState.step !== BulkPromoteStep.DONE;
        if (liveBatchId && batchIsOpen) {
            abandonPromote(liveBatchId);
            return;
        }
        promoteDispatch(ACTIONS.RESET_STATE);
    }

    // Offered before anything is in flight, and again whenever a push has stopped arriving — the
    // moments the progress panel tells the operator to use it. Not offered mid-flight otherwise,
    // where a read-back would only restate what the pushes are already saying.
    const recoveryOffered =
        promoteState.step == BulkPromoteStep.INPUT || promoteState.pushLost || promoteState.verdictOverdue;

    /*
     * Gated on the step, not only on `loading`. VERDICT_OVERDUE and PUSH_LOST both clear `loading` while
     * leaving the step at AWAITING_VERDICT or EXECUTING, so at the patience timeout — or on any socket error
     * mid-execution — this button and Clear came back to life over a batch that was still open, directly
     * below an alert warning against exactly that. The service refuses a second batch account-wide, but the
     * resubmit still nulls batchId and so discards this page's only handle on the batch holding the lock.
     *
     * overCapBy is checked here too: it was computed on every keystroke and read only after the click, so an
     * over-cap paste looked accepted until submit refused it.
     */
    const submitDisabled = isPromote
        ? promoteState.step !== BulkPromoteStep.INPUT
          || promoteState.loading
          || !promoteState.componentType
          || promoteState.acceptedIds.length == 0
          || promoteState.overCapBy > 0
        : loading || calculations == null || calculations.trim() == '';

    return <BulkPromotionContext.Provider value={{ services, bulkPromotionState: promoteState, bulkPromotionDispatch: promoteDispatch }}>
        <div className="bulkActionContainer">
        <SpaceBetween direction="horizontal" size="m">
            <SpaceBetween direction="vertical" size="xl">
                <FormField label="Select bulk action">
                    <ATPHorizontalRadioGroup
                        data-class="basicModalRadioGroup"
                        onChange={setSelectedAction}
                        value={selectedAction}
                        items={bulkActionOptions}
                    />
                </FormField>
                <FormField data-class="bulkActionCheckbox">
                    <Checkbox onChange={({ detail }) => setChecked(detail.checked)} checked={checked} disabled={['VALIDATE', 'CREATE_DRAFT', BULK_PROMOTE_ACTION].includes(selectedAction)}>
                        {selectedAction == 'APPROVE' ? 'Only process calculations for which I am the workbook reviewer.'
                        : 'Only process calculations for which I am the calc assignee or builder.'} (Applicable only for APTTUS CLIs)
                    </Checkbox>
                </FormField>
                <div className="bulkActionBar">
                    <SpaceBetween direction="horizontal" size="m">
                        <Button variant="primary" disabled={submitDisabled} onClick={process}>
                            {(isPromote ? promoteState.loading : loading) ? <StatusIndicator type="loading">Processing</StatusIndicator>  : ACTION_BUTTON_LABEL[selectedAction] }
                        </Button>
                        <Button onClick={clear} disabled={isPromote ? promoteState.loading : loading}>Clear</Button>
                    </SpaceBetween>
                </div>
            </SpaceBetween>
            { isPromote
                ? <div data-class="calculationsTextArea"><BulkPromoteFields /></div>
                : <FormField data-class="calculationsTextArea" label={selectedAction == 'VALIDATE' ? 'Enter group number' : 'Enter calculation numbers'}>
                    <Textarea
                        onChange={({ detail }) => setCalculations(detail.value)}
                        value={calculations}
                        placeholder={selectedAction == 'VALIDATE' ? 'Enter a group number' : 'Enter calculation numbers separated by commas'}
                        rows={7}
                        />
                  </FormField>
            }
        </SpaceBetween>
        { isPromote &&
            <SpaceBetween direction="vertical" size="l">
                { batchHandle != null && recoveryOffered &&
                    <Alert
                        type={promoteState.pushLost || promoteState.verdictOverdue ? 'warning' : 'info'}
                        header={promoteState.pushLost
                            ? 'This batch can no longer report back to this page'
                            : promoteState.verdictOverdue
                              ? 'No verdict yet for this batch'
                              : 'You have an earlier batch'}
                        action={
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button onClick={recoverBatch} disabled={promoteState.loading}>
                                    Get its outcome
                                </Button>
                                {/*
                                  * Release, offered here because this banner is where an operator lands with a
                                  * batch they no longer want. A parked batch holds a lock the service applies
                                  * across every component type and accounting period, so leaving one sitting
                                  * refuses everyone else's bulk promotions until its consent window lapses.
                                  *
                                  * The service refuses this for a batch that has already been confirmed — its
                                  * components are mid-promotion and there is no pending consent to withdraw —
                                  * and says so, which is better than hiding the button and leaving the
                                  * operator without an explanation.
                                  */}
                                <Button onClick={() => abandonPromote(batchHandle.batchId)}
                                        disabled={promoteState.loading}>
                                    Release it
                                </Button>
                            </SpaceBetween>
                        }
                    >
                        Batch {batchHandle.batchId} was submitted from this browser on {batchHandle.submittedAt}.
                        {/*
                          * Deliberately does not claim the batch is running. This banner is drawn from a
                          * localStorage handle, which says only that this browser once submitted the id — it
                          * survives the batch finishing, expiring, or never having existed. It previously
                          * asserted "It is still running on the server" and said that about a batch with no
                          * record at all, which both misleads and, by warning against resubmitting, can stall
                          * an operator mid-close over work that is not in flight. What is true either way is
                          * that reading it back is free and resubmitting is not.
                          */}
                        {(promoteState.pushLost || promoteState.verdictOverdue) &&
                            ' Its outcome has not reached this page, which does not by itself mean the batch is' +
                            ' still running. Read it back to find out — that is safe and changes nothing,' +
                            ' whereas submitting again would start a second batch against the same components.'}
                    </Alert>
                }
                <IdentifierSummary />
                <BulkPromoteProgress />
                <BulkPromoteResults />
            </SpaceBetween>
        }
        { ['SUBMIT', 'APPROVE'].includes(selectedAction) && (progressUpdate != null || result != null) && 
            <div className="resultsDiv">
                <div className="innerResultsDiv">
                {progressUpdate != null && <span>{progressUpdate}</span>}
                {result != null &&
                    <React.Fragment>
                        <span><b>{result.numSuccessfulCalculations}</b> calculations successfully processed.</span>
                        {result.errors != null && result.errors.size > 0 &&
                            <React.Fragment>
                                <br /><br />
                                The following calculations were not successfully processed.
                                <br/><br/>
                                <ul>
                                    {Array.from((result.errors as Map<string, string>).keys()).map((k: string) => <li><b>{k}: </b>{result.errors.get(k) || ''}</li>)}
                                </ul>
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
                </div>
            </div>
        }
        { selectedAction == 'CREATE_DRAFT' && (progressUpdate != null || result != null) && 
            <div className="resultsDiv">
                <div className="innerResultsDiv">
                {progressUpdate != null && <span>{progressUpdate}</span>}
                {result != null &&
                    <React.Fragment>
                        <span><b>{result.activeCalcs[0]}</b> active calculations moved to draft.</span>
                        <br/>
                        <span><b>{result.pendingCalcs[0]}</b> pending calculations moved to draft.</span>
                        {result.errors != null && result.errors.size > 0 &&
                            <React.Fragment>
                                <br /><br />
                                The following calculations were not processed.
                                <br/><br/>
                                <ul>
                                    {Array.from((result.errors as Map<string, string>).keys()).map((k: string) => <li><b>{k}: </b>{result.errors.get(k) || ''}</li>)}
                                </ul>
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
                </div>
            </div>
        }
        { selectedAction == 'VALIDATE' && (progressUpdate != null || result != null) && 
            <div className="resultsDiv">
                <div className="innerResultsDiv">
                {progressUpdate != null && <span>{progressUpdate}</span>}
                {result != null &&
                    <React.Fragment>
                        <span>This group contains <b>{result.activeCalcs.length}</b> active calculations and <b>{result.draftCalcs.length}</b> non-active calculations.</span>
                        {result.draftCalcs.length > 0 &&
                            <React.Fragment>
                                <br /><br />
                                <b><u>Non-active calculations</u></b>
                                <br/><br/>
                                <ul>
                                    {result.draftCalcs.map((c: string) => <li>{c}</li>)}
                                </ul>
                            </React.Fragment>
                        }
                        {result.activeCalcs.length > 0 &&
                            <React.Fragment>
                                <br /><br />
                                <b><u>Active calculations</u></b>
                                <br/><br/>
                                <ul>
                                    {result.activeCalcs.map((c: string) => <li>{c}</li>)}
                                </ul>
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
                </div>
            </div>
        }
        <TPEBasicModal
            title={selectedAction == bulkActionOptions[0].value ? 'Bulk submit confirmation' : 'Bulk approve confirmation'}
            action={ACTION_TYPE.CONFIRM_CANCEL}
            visible={showConfirmation}
            onConfirm={submitForm}
            onCancel={() => setShowConfirmation(false)}
        >
            By clicking <b>Confirm</b> you agree that you have reviewed the calculations you have provided and choose to
            {selectedAction == bulkActionOptions[0].value ? ' submit them for review.' : ' approve them.'}
            {!checked && ' Note that you have chosen to ' + 
                (selectedAction == bulkActionOptions[0].value ? 'submit calculations for which you might not be the calc assignee or builder.' :
                'approve calculations for which you might not be the workbook reviewer.')
            }
            <br/><br/>
            Click on <b>Confirm</b> to proceed.
        </TPEBasicModal>
        <TPEBasicModal
            title="Bulk promote confirmation"
            action={ACTION_TYPE.CONFIRM_CANCEL}
            visible={promoteState.step == BulkPromoteStep.CONFIRMING && promoteState.preflight != null}
            onConfirm={confirmPromote}
            onCancel={() => abandonPromote(promoteState.batchId)}
            /*
             * Dismissing is not cancelling here. Cancel releases the batch on the server — the verdict is
             * gone and the preflight has to be re-run — and Cloudscape fires onDismiss for Escape and for a
             * click on the overlay as well as the close icon. Leaving them wired together meant a stray
             * click beside the dialog, while scrolling a long blocked list, abandoned the batch. Dismissing
             * now leaves the prompt and the batch exactly as they were; the recovery banner still offers
             * both "Get its outcome" and "Release it".
             */
            onDismiss={() => undefined}
        >
            <PreflightSummary />
        </TPEBasicModal>
        </div>
    </BulkPromotionContext.Provider>
}

function setupWebSocketEvents(services: ServiceCollection, setProgressUpdate: (string: any) => any,
    setResult: (result: any) => any, setLoading: (loading: boolean) => any,
    promoteDispatch: (action: any) => void, isPromoteAwaiting: () => boolean) {
    const listener = (x:any) => {
        const webSocketMessage = JSON.parse(x);

        // The preflight verdict is the one message on this connection with no envelope around it.
        // BulkReportComponent posts the PreflightOperatorReport object straight through the accessor,
        // so it has no messageType, no status and no `message` string — it is the body itself. Tested
        // before the messageType gate below, which would otherwise drop every frame of it.
        if (isPreflightOperatorReport(webSocketMessage)) {
            promoteDispatch(ACTIONS.VERDICT_FRAME.withPayload(webSocketMessage));
            return;
        }

        //Listen for messages from bulk reversal API
        if (webSocketMessage?.messageType == CONSTANTS.WEB_SOCKET_API_ROUTES.PERFORM_BULK_ACTION) {
            const promotePayload = parseBulkPromoteMessage(webSocketMessage.message);
            if (isPromoteEnvelope(webSocketMessage, promotePayload)) {
                handlePromoteMessage(services, webSocketMessage, promoteDispatch);
                return;
            }
            // A promote flow that is mid-flight owns any failure on this route: the failure envelope
            // carries no batch id or verdict to recognise it by, so without this the promote spinner
            // would run forever while the error surfaced against the unrelated calculation flow.
            if (webSocketMessage.status === CONSTANTS.PROCESSING_STATUS.FAILED && isPromoteAwaiting()) {
                handlePromoteMessage(services, webSocketMessage, promoteDispatch);
                /*
                 * The calculation flow's spinner is cleared too, because this arm cannot tell whose failure
                 * it is taking. The envelope carries no batch id, so a failure belonging to a bulk submit or
                 * approve run in the same tab is claimed here as well — and handlePromoteMessage has no
                 * access to setLoading, so that flow was left with a dead button and a dead Clear until the
                 * page was reloaded. Clearing both is the safe direction: a spinner stopped early is
                 * recoverable, one that never stops is not.
                 */
                setLoading(false);
                return;
            }
            if (webSocketMessage.status === CONSTANTS.PROCESSING_STATUS.FAILED) {
                services.messageService.showErrorBanner(webSocketMessage.message);
                setLoading(false);
            } else if (webSocketMessage.status === CONSTANTS.PROCESSING_STATUS.IN_PROGRESS) {
                setProgressUpdate(webSocketMessage.message);
            } else if (webSocketMessage.status === CONSTANTS.PROCESSING_STATUS.SUCCEEDED) {
                const jsonResult = JSON.parse(webSocketMessage.message);
                if (jsonResult.errors != null) {
                    jsonResult.errors = new Map(Object.entries(jsonResult.errors));
                    jsonResult.errors.forEach((v: string, k: string) => console.log(k, v));
                }
                setResult(jsonResult);
                setProgressUpdate(null as unknown as string);
                setLoading(false);
            }
        }
    };

    const errorHandler = (x:any) => {
        services.messageService.showErrorBanner(CONSTANTS.WEBSOCKET_ERROR_MESSAGE);
        // The batch itself is unaffected — it runs in Step Functions, not in this socket — but its
        // push was addressed to a connection id that is now gone and will not be redelivered. So the
        // promote flow is parked rather than failed, and pointed at the read-back path.
        promoteDispatch(ACTIONS.PUSH_LOST);
        setTimeout(() => {
            setResult(null);
            setProgressUpdate(null as unknown as string);
            setLoading(false);
        }, 500);
    }

    services.atpWebSocketApiService.addListener(listener);
    services.atpWebSocketApiService.addKeyListener(WebSocketApi.ConnectionEvent.ON_ERROR, errorHandler);
    // Both handlers are returned so both can be removed on unmount. The keyed one was previously
    // left behind, and the client is a single long-lived instance whose listeners are only cleared
    // by disconnect(), so each mount added another error handler to the shared client.
    return { listener, errorHandler };
}

export function parseBulkPromoteMessage(rawMessage: any): BulkPromoteProgressMessage {
    if (rawMessage == null) {
        return {};
    }
    if (typeof rawMessage !== 'string') {
        return rawMessage as BulkPromoteProgressMessage;
    }
    try {
        return JSON.parse(rawMessage) as BulkPromoteProgressMessage;
    } catch (ex) {
        return {};
    }
}

function handlePromoteMessage(services: ServiceCollection, webSocketMessage: any,
    promoteDispatch: (action: any) => void) {
    if (webSocketMessage.status === CONSTANTS.PROCESSING_STATUS.FAILED) {
        services.messageService.showErrorBanner(webSocketMessage.message);
        promoteDispatch(ACTIONS.SET_FAILED.withPayload(webSocketMessage.message));
        return;
    }

    const payload = parseBulkPromoteMessage(webSocketMessage.message);
    const preflight = readPreflight(payload);
    const execution = readExecution(payload);
    if (payload.batchId != null) {
        promoteDispatch(ACTIONS.PREFLIGHT_ACCEPTED.withPayload({
            batchId: payload.batchId,
            // PerformBulkActionResponse carries no acceptedCount of its own today, so the verdict's
            // is the fallback. Zero only when neither is present, which reads as "not yet known".
            acceptedCount: payload.acceptedCount ?? preflight?.acceptedCount ?? 0,
        }));
    }
    if (payload.phase != null) {
        promoteDispatch(ACTIONS.SET_PHASE.withPayload({ phase: payload.phase, barrierWaitMillis: payload.barrierWaitMillis }));
    }
    if (preflight != null && isCompleteVerdict(preflight)) {
        promoteDispatch(ACTIONS.SET_VERDICT.withPayload(preflight));
    }
    // An execution payload without outcomes is the acknowledgement that the batch started, and the
    // batch id on it has already been taken above. Staying on the progress view keeps the operator
    // watching a batch that is genuinely still running.
    if (execution != null && isFinishedExecution(execution)) {
        promoteDispatch(ACTIONS.EXECUTION_COMPLETE.withPayload(execution));
    }
}